'use client';

import { useRouter } from 'next/navigation';
import {
  CardImage,
  HeadlineRow,
  ImageContainer,
  PriceContainer,
  PriceLabel,
  PriceValue,
  StockCount,
  Title
} from '@/new-cars/common/ConfiguratorCard.styles';
import { TileLink } from '@/new-cars/common/TileLink.styles';
import CustomConfigs from '~/constants/customConfigs';
import type { MakeResult } from '~/contracts/new-cars';
import useTestDriveCenter from '~/hooks/new-cars/useTestDriveCenter';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { isFallbackImage } from '~/utilities/isFallbackImage';
import optimizeCardImage from '~/utilities/new-cars/optimizeCardImage';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';

type OwnProps = {
  make: string;
  parentModel: MakeResult;
};

export default function ParentModelCard({ make, parentModel }: OwnProps) {
  const router = useRouter();
  const { sendCube, session } = useTestDriveCenter();

  const { count, imageUrl, lowestPrice, title } = parentModel;
  const isFallback = isFallbackImage(imageUrl);
  const model = parentModel.next.parentModel;
  const base = `/new/${toLowerKebabCase(make)}/${toLowerKebabCase(model)}`;
  const href = session ? `${base}?session=${session}&year=2026` : base;
  const optimizedImageUrl = optimizeCardImage(imageUrl, isFallback);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Set cookie so the RSC request for the new route suppresses the header
    // server-side. ParentModelContainer clears it on mount.
    document.cookie = `${CustomConfigs.RemoveHeader}=1; path=/; max-age=10`;

    if (session) {
      e.preventDefault();
      // Model chosen: advance the paired cube display to the "build" screen,
      // then navigate to the configurator preserving the session and pinned
      // year so the server fetches the right configuration.
      sendCube({ make, model, screen: 'build' });
      router.push(href);
    }
  };

  return (
    <TileLink
      aria-label={title}
      data-analytics-attributes={JSON.stringify({
        level: 'Parent Model',
        section: 'Parent Model',
        text: title
      })}
      data-analytics-id="New Cars"
      data-testid="parent-model-card"
      href={href}
      onClick={handleClick}
    >
      <ImageContainer
        aria-hidden="true"
        disabled={false}
        isFallback={isFallback}
      >
        <CardImage
          alt={title}
          data-testid="parent-model-card-image"
          src={optimizedImageUrl}
        />
      </ImageContainer>
      <div className="flex flex-col gap-md">
        <HeadlineRow>
          <Title disabled={false}>{title}</Title>
          <PriceContainer data-testid="parent-model-card-price">
            <PriceLabel>from </PriceLabel>
            <PriceValue disabled={false}>
              ${formatAndDropDecimal(lowestPrice)}
            </PriceValue>
          </PriceContainer>
        </HeadlineRow>
        <StockCount data-testid="parent-model-card-stock-count">
          {count} in stock
        </StockCount>
      </div>
    </TileLink>
  );
}
